import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import StarRating from '@alltrails/denali/components/StarRating';
import { useIntl } from 'react-intl';
import Texture from '@alltrails/shared/denali/components/Texture';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import Button from '@alltrails/denali/components/Button';
import Close from '@alltrails/denali/icons/Close';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import { useEffect, useState } from 'react';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import { Image, ModalPortal, Typography, isAndroid, useScreenLock } from '@alltrails/core';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
// eslint-disable-next-line @alltrails/custom/client-navigation
import { useSearchParams } from 'next/navigation';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { StaticImageData } from 'next/image';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import appIconSrc from '../../assets/app-icon.avif';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import styles from './InstallInterstitial.module.scss';
import getInterstitialContent, { InterstitialVariants } from './constants';

type InstallInterstitialProps = {
  promptLocation: InstallPromptLocation;
  deeplinkData?: DeeplinkData;
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
  isScrollLimitActive?: boolean;
  isFocusPaused?: boolean;
} & (
  | { altText?: never; header?: never; phoneImg?: never; variant: InterstitialVariants }
  | { altText: string; header: string; phoneImg: StaticImageData; variant?: never }
);

// Design link: https://www.figma.com/file/oevB4bXh8KLLDe1Yp8rBW2?type=design%27&node-id=11925:74124

const InstallInterstitial = ({
  altText,
  deeplinkData,
  displayTrigger,
  header,
  isOpen: isOpenAttempt = false,
  isScrollLimitActive,
  isFocusPaused = false,
  onRequestClose,
  phoneImg,
  promptLocation,
  variant
}: InstallInterstitialProps) => {
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const [isMounted, setIsMounted] = useState(false);
  const isOpen = isMounted && isOpenAttempt && isMobile;
  const getDeeplink = useDeeplink({ deeplinkData, isOpen, promptType: 'interstitial', displayTrigger });
  const languageRegionCode = useLanguageRegionCode();
  useScreenLock(isOpen);
  const sendExposure = useExposureEvent('web-appsflyer-migration');

  useEffect(() => {
    if (isOpen && isMobile) {
      sendExposure();
    }
  }, [isOpen, isMobile, sendExposure]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: 'interstitial',
        install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native',
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isAppsFlyerEnabled, isOpen, promptLocation]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: 'interstitial',
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
    });
    onRequestClose?.();
  };

  let searchParamsVariant;
  const params = useSearchParams();

  if (params) {
    searchParamsVariant = params.get('variant');
  }

  const content = variant ? getInterstitialContent(searchParamsVariant || variant, languageRegionCode, intl) : { altText, header, phoneImg };

  return isOpen ? (
    <ModalPortal>
      <FocusTrap
        active={isOpen}
        paused={isFocusPaused}
        // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
        focusTrapOptions={{
          fallbackFocus: '#interstitial-container'
        }}
      >
        <div
          className={classNames(
            {
              [styles.containerWithFadeIn]: isScrollLimitActive
            },
            styles.container
          )}
          id="interstitial-container"
        >
          <div className={styles.imageContainer}>
            {variant === 'generic' && <Texture variant="leaves" imageSize="full" className={styles.texture} />}

            <Image src={content.phoneImg} alt={content.altText} className={styles.phone} quality={100} />
          </div>
          <div className={styles.outerContainer}>
            <div className={styles.contentContainer}>
              <Typography variant="heading500" className={styles.header}>
                {content.header}
              </Typography>
              <div className={styles.divider} />
            </div>

            <IconButton
              className={classNames(styles.close)}
              icon={{ Component: Close }}
              onClick={handleCloseClick}
              size="md"
              title={intl.formatMessage({ defaultMessage: 'Close' })}
              testId="close-button"
              variant="flat"
            />
            <div className={styles.contentContainer}>
              <div className={styles.logoAndRatingContainer}>
                <Image src={appIconSrc} alt={intl.formatMessage({ defaultMessage: 'AllTrails logo' })} quality={100} />
                <div className={styles.nameAndRatingContainer}>
                  <Typography variant="heading200">AllTrails</Typography>
                  <div className={styles.ratingContainer}>
                    <StarRating rating={5} variant="complex" />
                    <Typography variant="text100" component="span">
                      {/* TODO replace with dynamic ratings https://alltrails.atlassian.net/browse/GROW-4286 */}(
                      {isMounted && isAndroid() ? 4.8 : 4.9})
                    </Typography>
                  </div>
                </div>
              </div>
              <Button
                text={intl.formatMessage({ defaultMessage: 'Continue in app' })}
                onClick={() => {
                  deepLinkRedirect(
                    getDeeplink(),
                    'interstitial',
                    promptLocation,
                    isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native',
                    displayTrigger
                  );
                }}
                testId="banner-button"
                variant="primary"
                size="md"
                allowWrap
                width="fullOnMobile"
              />
            </div>
          </div>
        </div>
      </FocusTrap>
    </ModalPortal>
  ) : null;
};

export default InstallInterstitial;
